import type {
    Characteristic,
    CharacteristicBase,
    CharacteristicVisibility,
    ChoicePriceMatrixConfig,
    MatrixData,
    PackageValueRef,
    ValuePriceOperator,
} from '../schema/characteristic';
import { parseValueArray } from './parseValue';
import { parsePricelistArray } from './parsePricelist';
import { parseBlacklistArray } from './parseBlacklist';
import { parseCustomFieldTexts } from './parseCustomFields';
import {
    boolFromStringFlag,
    imageUrlField,
    intFromMixed,
    localized,
    numericString,
    optionalString,
} from './coerce';

const seenUnknownTypes = new Set<string>();

export function parseCharacteristicArray(raw: unknown): Characteristic[] {
    if (!Array.isArray(raw)) return [];
    // Sort by sort_order — same reasoning as parseGroupArray / parseValueArray:
    // the admin Drag&Drop only writes new sort_order values onto existing
    // items; we have to normalise the order at parse time.
    return raw.map(parseCharacteristic).sort((a, b) => a.sortOrder - b.sortOrder);
}

function parseCharacteristic(raw: unknown): Characteristic {
    const r = (raw ?? {}) as Record<string, unknown>;
    const base = parseBase(r);
    const type = typeof r.type === 'string' ? r.type : 'unknown';

    switch (type) {
        // ── Selection-based ──────────────────────────────────────────────
        case 'select':
            // Legacy `placeholder` on a select-type is a per-field boolean
            // flag the admin writes for every Characteristic ("0"/"1");
            // it carries no real text for the dropdown prompt, so we drop
            // it and always use the localised default `t('select.placeholder')`.
            return {
                ...base,
                ...parseSelectionFields(r),
                type: 'select',
                ...parseQuantityFields(r),
                operatorValue: optionalString(r.operator_value),
            };
        case 'multiselect':
            return { ...base, ...parseSelectionFields(r), type: 'multiselect' };
        case 'radiobuttons':
            return {
                ...base,
                ...parseSelectionFields(r),
                type: 'radiobuttons',
                ...parseQuantityFields(r),
                valueTooltip: boolFromStringFlag(r.choiceTooltip),
            };
        case 'checkbox':
            return {
                ...base,
                ...parseSelectionFields(r),
                type: 'checkbox',
                ...parseQuantityFields(r),
                valueTooltip: boolFromStringFlag(r.choiceTooltip),
            };
        case 'listimage':
            return {
                ...base,
                ...parseSelectionFields(r),
                type: 'listimage',
                ...parseQuantityFields(r),
                isMultiple: boolFromStringFlag(r.is_multiple),
                listImageStyle: parseListImageStyle(r.listimage_style),
                thumbStyle: parseThumbStyle(r.thumbstyle),
                itemsPerLine: r.listimage_items_per_line != null
                    ? intFromMixed(r.listimage_items_per_line, 0)
                    : undefined,
                hover: boolFromStringFlag(r.listimage_hover),
            };
        case 'multiquantity':
            return {
                ...base,
                ...parseSelectionFields(r),
                type: 'multiquantity',
                quantity: intFromMixed(r.quantity, 0),
                quantityPerItem: boolFromStringFlag(r.quantity_per_item),
            };

        // ── Free-entry ───────────────────────────────────────────────────
        case 'text':
            return {
                ...base,
                ...parseFreeEntryFields(r),
                type: 'text',
                textValidate: optionalString(r.text_validate),
                minValue: numericString(r.min_value),
                maxValue: numericString(r.max_value),
                decimalPlace: intFromMixed(r.decimal_place, 0),
                minFiles: r.min_files != null ? intFromMixed(r.min_files, 0) : undefined,
                maxFiles: r.max_files != null ? intFromMixed(r.max_files, 0) : undefined,
                acceptedFiles: optionalString(r.accepted_files),
            };
        case 'area':
            return { ...base, ...parseFreeEntryFields(r), type: 'area' };

        // ── Calculated ───────────────────────────────────────────────────
        case 'expression':
            return {
                ...base,
                ...parseCalculatedFields(r),
                type: 'expression',
                expression: optionalString(r.expression),
            };
        case 'combi':
            return {
                ...base,
                ...parseCalculatedFields(r),
                type: 'combi',
                combiFields: parseCombiFields(r.combifields),
                operatorValue: optionalString(r.operator_value),
                minValue: numericString(r.min_value),
                maxValue: numericString(r.max_value),
            };
        case 'matrixvalue': {
            // Canonical wire location for matrix dimension/operator/control
            // settings is NESTED under `matrix.dimension_x`, `matrix.operator_x`,
            // `matrix.free_input_x`, `matrix.control_matrix`. That's what the
            // legacy storefront (configento/app/storage.js:243-271) reads and
            // what the admin editor + AI tool have always written. Top-level
            // `matrix_dimension_x` exists as a Java DTO field on the cloud
            // (PcTemplateV3/V4) but it's an API-shape detail, not the
            // persisted shape — reading it as primary breaks any component
            // saved through the editor.
            //
            // Top-level lookup is preserved as a fallback ONLY for the small
            // window of components that were accidentally migrated to the
            // top-level location by an earlier (now-reverted) version of
            // the admin / AI / REST API. Those will be cleaned up on next
            // save in the corrected editor.
            const m = (r.matrix ?? {}) as Record<string, unknown>;
            const altTitle = typeof r.alt_title === 'string' ? r.alt_title : `#${r.id}`;
            console.debug('[configento] matrixvalue wire fields', altTitle, {
                'matrix.dimension_x':  m.dimension_x,
                'matrix.operator_x':   m.operator_x,
                'matrix.dimension_y':  m.dimension_y,
                'matrix.operator_y':   m.operator_y,
                'matrix.control_matrix': m.control_matrix,
                'matrix.values length': Array.isArray(m.values) ? (m.values as unknown[]).length : 0,
                'top-level matrix_dimension_x (fallback)': r.matrix_dimension_x,
                'top-level matrix_dimension_y (fallback)': r.matrix_dimension_y,
            });
            return {
                ...base,
                ...parseCalculatedFields(r),
                type: 'matrixvalue',
                matrix: parseMatrixData(r.matrix),
                matrixDimensionX: optionalString(m.dimension_x) ?? optionalString(r.matrix_dimension_x),
                matrixOperatorX:  optionalString(m.operator_x)  ?? optionalString(r.matrix_operator_x),
                matrixDimensionY: optionalString(m.dimension_y) ?? optionalString(r.matrix_dimension_y),
                matrixOperatorY:  optionalString(m.operator_y)  ?? optionalString(r.matrix_operator_y),
                matrixFreeInputX: optionalString(m.free_input_x) ?? optionalString(r.matrix_free_input_x),
                matrixFreeInputY: optionalString(m.free_input_y) ?? optionalString(r.matrix_free_input_y),
                matrixCsvDelimiter: optionalString(r.matrix_csv_delimiter),
                matrixFilename:     optionalString(r.matrix_filename),
                controlMatrix: optionalString(m.control_matrix) ?? optionalString(r.control_matrix),
            };
        }
        case 'productattribute':
            return {
                ...base,
                ...parseCalculatedFields(r),
                type: 'productattribute',
                productAttribute: optionalString(r.product_attribute),
            };
        case 'webservice':
            return {
                ...base,
                ...parseCalculatedFields(r),
                type: 'webservice',
                webserviceUrl: optionalString(r.webservice_url ?? r.url),
                webserviceReturnType: optionalString(r.webservice_return_type),
                // Legacy typo: `webservie_path` — normalise.
                webservicePath: optionalString(r.webservice_path ?? r.webservie_path),
            };
        case 'package':
            return {
                ...base,
                type: 'package',
                valueImageVisibility: parseValueImageVisibility(r.choiceImageVisibility),
                packagePrice: numericString(r.package_price ?? r.price),
                packageShadowOptions: boolFromStringFlag(r.package_shadow_options),
                groupLayer: optionalString(r.group_layer),
                layerPosition: optionalString(r.layer_position),
                packageValues: parsePackageValues(r.packageOptions),
                values: parseValueArray(r.choices),
            };

        // ── UI-only ──────────────────────────────────────────────────────
        case 'fileupload':
            return {
                ...base,
                type: 'fileupload',
                isRequired: boolFromStringFlag(r.is_required ?? r.is_require),
                minFiles: r.min_files != null ? intFromMixed(r.min_files, 0) : undefined,
                maxFiles: r.max_files != null ? intFromMixed(r.max_files, 0) : undefined,
                maxFilesize: numericString(r.max_filesize),
                acceptedFiles: optionalString(r.accepted_files),
            };
        case 'chart':
            return {
                ...base,
                type: 'chart',
                chartAspectRatio: optionalString(r.chartAspectRatio),
                chartType: optionalString(r.chartType),
                chartSeries: r.chartSeries,
                updateChartFromExpression: optionalString(r.updateChartFromExpression),
                extraOptions: r.extraOptions,
                responsiveOptions: r.responsiveOptions,
                onDrawOptions: r.onDrawOptions,
            };
        case 'savebutton':
            return {
                ...base,
                type: 'savebutton',
                saveAction: optionalString(r.save_action),
                postUrl: optionalString(r.post_url ?? r.url),
                redirectUrl: optionalString(r.redirect_url),
            };
        case 'static':
            return {
                ...base,
                type: 'static',
                defaultValue: localized(r.default_value),
            };
        case 'captcha':
            return {
                ...base,
                type: 'captcha',
                captchaProvider: 'altcha',
                captchaComplexity: parseCaptchaComplexity(r.captcha_complexity),
            };

        // ── Unknown fallback ─────────────────────────────────────────────
        default:
            if (!seenUnknownTypes.has(type)) {
                seenUnknownTypes.add(type);
                console.warn(`[Configento] unknown characteristic type "${type}" — rendered as Unknown.`);
            }
            return {
                ...base,
                type: 'unknown',
                originalType: type,
                raw,
            };
    }
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function parseBase(r: Record<string, unknown>): CharacteristicBase {
    return {
        id: intFromMixed(r.id, 0),
        altTitle: typeof r.alt_title === 'string' ? r.alt_title : '',
        title: localized(r.title),
        info: localized(r.info),
        moreInfo: localized(r.more_info),
        visibility: parseVisibility(r.is_visible),
        sortOrder: intFromMixed(r.sort_order, 0),
        sortOrderCombiImage: intFromMixed(r.sort_order_combiimage, 0),
        cssClass: optionalString(r.css_class),
        customFields: parseCustomFieldTexts(r.customFields ?? r.custom_fields),
        sku: optionalString(r.sku),
        price: numericString(r.price),
        operatorValuePrice: parseValuePriceOperator(r.operator_value_price),
        // Wire field is `image` — that's what the admin upload widget writes,
        // what the publish-translator writes, and what PHP `enrichImageUrls`
        // rewrites into absolute URLs. `option_image` is a legacy alias kept
        // as fallback for old imports / translator-renamed payloads that
        // didn't go through the V3 upload path.
        optionImage: imageUrlField(r.image ?? r.option_image),
    };
}

function parseSelectionFields(r: Record<string, unknown>) {
    const values = parseValueArray(r.choices);
    return {
        values,
        blacklist: parseBlacklistArray(r.blacklist),
        isRequired: boolFromStringFlag(r.is_required ?? r.is_require),
        valueImageVisibility: parseValueImageVisibility(r.choiceImageVisibility),
        choicePriceMatrix: parseChoicePriceMatrix(r),
        defaultValueId: resolveDefaultValueId(r.default_value, values),
    };
}

function parseChoicePriceMatrix(r: Record<string, unknown>): ChoicePriceMatrixConfig | undefined {
    if (! boolFromStringFlag(r.choice_price_matrix)) return undefined;
    const cfg = r.choice_matrix;
    if (!cfg || typeof cfg !== 'object') return undefined;
    const c = cfg as Record<string, unknown>;
    const matrix = optionalString(c.matrix);
    const dimensionX = optionalString(c.dimension_x);
    const dimensionY = optionalString(c.dimension_y);
    if (!matrix || !dimensionX || !dimensionY) return undefined;
    return { matrix, dimensionX, dimensionY };
}

/**
 * Match the legacy `default_value` string against `choice.value` to obtain
 * the initial Value id. Returns undefined for unset / unmatched defaults.
 * Strings like `'0'` are taken literally — admin sometimes uses `'0'` as a
 * sentinel value, but the only safe interpretation is "look for a Value
 * whose value field equals that string".
 */
function resolveDefaultValueId(raw: unknown, values: ReturnType<typeof parseValueArray>): number | undefined {
    if (raw == null || raw === '') return undefined;
    const needle = String(raw);
    for (const v of values) {
        if (v.value === needle) return v.id;
    }
    // Numeric fallback: admin sometimes stores choice id directly.
    const n = Number(needle);
    if (Number.isFinite(n) && values.some((v) => v.id === n)) return n;
    return undefined;
}

function parseFreeEntryFields(r: Record<string, unknown>) {
    return {
        isRequired: boolFromStringFlag(r.is_required ?? r.is_require),
        useDefaultAsPlaceholder: boolFromStringFlag(r.placeholder),
        maxCharacters: numericString(r.max_characters),
        minStringLength: numericString(r.min_string_length),
        maxStringLength: numericString(r.max_string_length),
        defaultValue: localized(r.default_value),
        pricelist: parsePricelistArray(r.pricelist),
    };
}

function parseCalculatedFields(r: Record<string, unknown>) {
    return {
        pricelist: parsePricelistArray(r.pricelist),
        decimalPlace: intFromMixed(r.decimal_place, 0),
    };
}

function parseListImageStyle(v: unknown): '0' | '1' | undefined {
    if (v === '1' || v === 1) return '1';
    if (v === '0' || v === 0) return '0';
    return undefined;
}

function parseThumbStyle(v: unknown): 'inline' | 'collapse' | 'overlay' | undefined {
    if (v === 'inline' || v === 'collapse' || v === 'overlay') return v;
    return undefined;
}

function parseVisibility(v: unknown): CharacteristicVisibility {
    const s = typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '1';
    switch (s) {
        case '0': return 'hidden';
        case '2': return 'shop';
        case '3': return 'backend';
        default:  return 'visible';
    }
}

function parseValuePriceOperator(v: unknown): ValuePriceOperator {
    return v === '*' ? '*' : 'none';
}

function parseCaptchaComplexity(v: unknown): 'easy' | 'medium' | 'hard' {
    if (v === 'easy' || v === 'medium' || v === 'hard') return v;
    return 'medium';
}

function parseValueImageVisibility(v: unknown): 'hide' | 'selected' | 'always' {
    if (v === 'hide' || v === 'selected' || v === 'always') return v;
    return 'always';
}

function parseCombiFields(v: unknown): string[] {
    if (!Array.isArray(v)) return [];
    return v.filter((x): x is string => typeof x === 'string');
}

function parseMatrixData(v: unknown): MatrixData | undefined {
    if (v == null || typeof v !== 'object') return undefined;
    const m = v as Record<string, unknown>;
    if (!Array.isArray(m.values)) return undefined;
    // Keep one entry per uploaded table, even a broken one: the control list
    // selects tables by position, so dropping an entry would shift every
    // later table onto the wrong index.
    return {
        values: m.values.map((entry) => {
            const e = (entry ?? {}) as Record<string, unknown>;
            return {
                name: typeof e.name === 'string' ? e.name : '',
                data: Array.isArray(e.data) ? (e.data as unknown[][]) : [],
            };
        }),
    };
}

function parsePackageValues(v: unknown): PackageValueRef[] {
    if (!Array.isArray(v)) return [];
    return v.map((entry) => {
        const e = (entry ?? {}) as Record<string, unknown>;
        return {
            id: intFromMixed(e.id, 0),
            groupIndex: intFromMixed(e.optionGroupIndex, 0),
            characteristicIndex: intFromMixed(e.optionIndex, 0),
            availableValues: Array.isArray(e.availableChoices)
                ? e.availableChoices.map((x) => intFromMixed(x, 0))
                : [],
            defaultValueId: Array.isArray(e.defaultChoiceId)
                ? e.defaultChoiceId.map((x) => intFromMixed(x, 0))
                : [],
        };
    });
}

/** Admin "Menge" on select / radio / checkbox / image list (legacy `quantity`, `min_qty`, `max_qty`). */
function parseQuantityFields(r: Record<string, unknown>): {
    quantityMode: 0 | 1 | 2;
    minQty: number | undefined;
    maxQty: number | undefined;
} {
    const mode = intFromMixed(r.quantity, 0);
    const min = Number(r.min_qty);
    const max = Number(r.max_qty);
    return {
        quantityMode: mode === 1 || mode === 2 ? mode : 0,
        minQty: r.min_qty != null && r.min_qty !== '' && Number.isFinite(min) ? Math.max(0, Math.floor(min)) : undefined,
        maxQty: r.max_qty != null && r.max_qty !== '' && Number.isFinite(max) ? Math.floor(max) : undefined,
    };
}
